import { color } from "framer-motion";
import React, { useState } from "react";

const DecimalToBinaryStep = () => {
    const [decimal, setDecimal] = useState(13);
    const [steps, setSteps] = useState([]);
    const [stepIndex, setStepIndex] = useState(0);
    const [collectedRemainders, setCollectedRemainders] = useState([]);
    const [qVal, setQVal] = useState("");
    const [rVal, setRVal] = useState("");
    const [feedback, setFeedback] = useState("");
    const [finalAnswer, setFinalAnswer] = useState("");
    const [finalFeedback, setFinalFeedback] = useState("");
    const [showActivity, setShowActivity] = useState(false);
    const [showFinal, setShowFinal] = useState(false);
    const [showSolution, setShowSolution] = useState(false);

    const computeSteps = (n) => {
        const arr = [];
        let value = n;
        if (value === 0) {
            arr.push({ value: 0, quotient: 0, remainder: 0 });
            return arr;
        }
        while (value > 0) {
            const q = Math.floor(value / 2);
            const r = value % 2;
            arr.push({ value, quotient: q, remainder: r });
            value = q;
        }
        return arr;
    };

    const handleStart = () => {
        if (isNaN(decimal) || decimal < 0) {
            alert("Please enter a valid non-negative integer.");
            return;
        }
        const s = computeSteps(decimal);
        setSteps(s);
        setStepIndex(0);
        setCollectedRemainders([]);
        setFeedback("");
        setShowActivity(true);
        setShowFinal(false);
        setShowSolution(false);
    };

    const handleReset = () => {
        setDecimal(13);
        setSteps([]);
        setStepIndex(0);
        setCollectedRemainders([]);
        setFeedback("");
        setFinalAnswer("");
        setFinalFeedback("");
        setShowActivity(false);
        setShowFinal(false);
        setShowSolution(false);
    };

    const handleSubmitStep = () => {
        if (qVal === "" || rVal === "") {
            setFeedback("Please enter both quotient and remainder.");
            return;
        }
        const step = steps[stepIndex];
        const qNum = Number(qVal);
        const rNum = Number(rVal);
        if (!(rNum === 0 || rNum === 1)) {
            setFeedback("Remainder must be 0 or 1.");
            return;
        }
        if (qNum === step.quotient && rNum === step.remainder) {
            setCollectedRemainders((prev) => [...prev, rNum]);
            setStepIndex((prev) => prev + 1);
            setQVal("");
            setRVal("");
            setFeedback("");
            if (stepIndex + 1 >= steps.length) {
                setShowFinal(true);
            }
        } else {
            let hint = "That is not correct. Try again.";
            if (step.value % 2 === 0 && rNum !== 0)
                hint = "Hint: the number is even, remainder should be 0.";
            if (step.value % 2 === 1 && rNum !== 1)
                hint = "Hint: the number is odd, remainder should be 1.";
            setFeedback(hint);
        }
    };

    const handleCheckFinal = () => {
        if (!finalAnswer.trim()) {
            setFinalFeedback("Please type a binary number (e.g. 1101).");
            setShowSolution(false);
            return;
        }
        const correct = collectedRemainders.slice().reverse().join("");
        const correctBinary = decimal === 0 ? "0" : correct;
        if (finalAnswer.trim() === correctBinary) {
            setFinalFeedback("✅ Correct! Well done.");
            setShowSolution(true);
        } else {
            setFinalFeedback("❌ Not quite — try reading the remainders from bottom up.");
            setShowSolution(false);
        }
    };

    const handleShowSolution = () => {
        setShowSolution(true);
        setFinalFeedback("Solution revealed below.");
    };

    const correctBinary = decimal === 0 ? "0" : collectedRemainders.slice().reverse().join("");

    const styles = {
        body: {
            color: "black"
        },
        card: {
            width: "100%",
            maxWidth: "700px",
            background: "#0f111a",
            borderRadius: "16px",
            padding: "28px",
            margin: "28px auto",
            fontFamily: "'Fira Code', monospace",
            color: "#cfd8dc",
            boxShadow: "0 0 20px rgba(0, 255, 255, 0.2)",
            border: "1px solid #00fff7",
        },
        title: { color: "#00fff7", textAlign: "center", fontSize: "26px", marginBottom: "6px" },
        meta: { textAlign: "center", color: "white", marginBottom: "20px", fontSize: "18px" },
        row: {
            display: "flex",
            gap: "12px",
            justifyContent: "center",
            flexWrap: "wrap",
            marginBottom: "16px",
        },
        label: { fontWeight: 600, color: "#00fff7",fontSize:"18px" },
        input: {
            padding: "10px 14px",
            justifyContent:"centre",
               margin: "0 auto", 
            borderRadius: "10px",
            border: "1px solid #00fff7",
            fontSize: "18px",
            width: "130px",
            textAlign: "center",
            background: "#1c1e2a",
            color: "#fff",
        },
        button: {
            background: "#00fff7",
            color: "#0f111a",
            border: 0,
            padding: "10px 18px",
            borderRadius: "10px",
            cursor: "pointer",
            fontWeight: "700",
        },
        secondary: {
            background: "linear-gradient(120deg, #ff00ff, #00ffff)",
            color: "#0f111a",
            border: "none",
            borderRadius: "10px",
            padding: "10px 18px",
            fontWeight: "700",
            cursor: "pointer",
        },
        stepLine: {
            background: "#1c1e2a",
            border: "1px dashed #00fff7",
            padding: "12px 16px",
            marginBottom: "12px",
            borderRadius: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
        },
        feedbackOk: { color: "#27ae60", fontWeight: "700", marginTop: "6px" },
        feedbackBad: { color: "#ff3f3f", fontWeight: "700", marginTop: "6px" },
        hint: { color: "#888", fontSize: "13px", marginTop: "6px" },
        resultBinary: {
            fontFamily: "monospace",
            fontSize: "22px",
            padding: "8px 14px",
            borderRadius: "12px",
            border: "1px solid #00fff7",
            background: "#1c1e2a",
            display: "inline-block",
            marginTop: "8px",
            fontWeight: "700",
            color: "#00fff7",
        },
    };

    return (
        <div style={{
    minHeight: "100vh", // full viewport height
    backgroundColor: "#000", // true black
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start", // top-aligned
    paddingTop: "30px",
    color: "#cfd8dc", // default text color
  }}  >
            <div style={styles.card}>
                <h1 style={styles.title}>Decimal → Binary (Step-by-Step)</h1>
                <div style={styles.meta}>
                    Convert a decimal number to binary by filling each quotient and remainder.
                </div>

                <div style={styles.row}>
                    <label style={styles.label}>Decimal:</label>
                    <input
                        type="number"
                        min="0"
                        value={decimal}
                        onChange={(e) => setDecimal(Number(e.target.value))}
                        style={styles.input}
                    />
                    <button onClick={handleStart} style={styles.button}>
                        Start
                    </button>
                    <button onClick={handleReset} style={styles.secondary}>
                        Reset
                    </button>
                </div>

                {showActivity && (
                    <>
                        {stepIndex < steps.length && (
                            <div style={styles.stepLine}>
                                <div>
                                    <div style={{ fontWeight: 600, color: "#00fff7" }}>
                                        Step {stepIndex + 1}: {steps[stepIndex].value} ÷ 2 = ? R ?
                                    </div>
                                    <div style={styles.hint}>Enter quotient and remainder</div>
                                </div>
                                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                    <input
                                        type="number"
                                        placeholder="quotient"
                                        value={qVal}
                                        onChange={(e) => setQVal(e.target.value)}
                                        style={{ ...styles.input, width: "110px" }}
                                    />
                                    <input
                                        type="number"
                                        placeholder="remainder (0/1)"
                                        value={rVal}
                                        onChange={(e) => setRVal(e.target.value)}
                                        style={{ ...styles.input, width: "120px" }}
                                    />
                                    <button onClick={handleSubmitStep} style={styles.button}>
                                        Submit Step
                                    </button>
                                </div>
                            </div>
                        )}

                        {feedback && (
                            <div
                                style={
                                    feedback.includes("Hint") ? styles.hint : styles.feedbackBad
                                }
                            >
                                {feedback}
                            </div>
                        )}

                        {collectedRemainders.length > 0 && (
                            <div style={{ marginTop: "20px" }}>
                                {steps.slice(0, stepIndex).map((s, idx) => (
                                    <div key={idx} style={styles.stepLine}>
                                        <div>
                                            <strong  style={{color:"#00fff7"}}  >{s.value} ÷ 2</strong>
                                        </div>
                                        <div style={{ textAlign: "right" }}>
                                            <div style={{ fontWeight: 700, color: "#00fff7" }}>
                                                = {s.quotient} &nbsp; R {s.remainder}
                                            </div>
                                            <div style={styles.hint}>Recorded</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {showFinal && (
                            <div style={{ marginTop: "24px", textAlign: "center", color:"white" }}>
                                <div style={{color:"white",fontSize:"24"}}  >
                                    Reading the remainders from the bottom up, enter
                                    the final binary:
                                </div>
                                <input
                                    type="text"
                                    placeholder="e.g. 1101"
                                    value={finalAnswer}
                                    onChange={(e) => setFinalAnswer(e.target.value)}
                                    style={styles.input}
                                />
                                <div
                                    style={{
                                        marginTop: "14px",
                                        display: "flex",
                                        justifyContent: "center",
                                        gap: "12px",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <button onClick={handleCheckFinal} style={styles.button}>
                                        Check
                                    </button>
                                    <button onClick={handleShowSolution} style={styles.secondary}>
                                        Show Solution
                                    </button>
                                </div>

                                {finalFeedback && (
                                    <p
                                        style={
                                            finalFeedback.includes("✅")
                                                ? styles.feedbackOk
                                                : styles.feedbackBad
                                        }
                                    >
                                        {finalFeedback}
                                    </p>
                                )}

                                {showSolution && (
                                    <div style={{ marginTop: "12px" }}>
                                        <div style={styles.hint}>Correct binary:</div>
                                        <div style={styles.resultBinary}>{correctBinary}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DecimalToBinaryStep;
